import { Component, createRef } from "react"
import PropTypes from "prop-types"
import noop from "lodash/noop"
import { ReactSVG } from "react-svg"
import ReactPlayer from "react-player"
import getContentUrl from "utils-lib/getContentUrl"
import { Button, GenericModal } from "component-lib/Base"
import { withStyles } from "tss-react/mui"
import { getWindow } from "utils-lib/getWindow"

const ssrWindow = getWindow()

const styles = () => ({
  videoStyl: {
    position: `relative`,
  },
  buttonStyl: {
    position: `absolute`,
    top: "50%",
    left: "50%",
    transform: `translate(-50%,-50%)`,
  },
})

/*
  This component can render a video content file or a YouTube video link.
*/
class Video extends Component {
  constructor(props) {
    super(props)

    this.state = {
      open: false,
      isPlaying: true,
    }
    this.videoRef = createRef()
  }

  handleOpenModal = () => {
    const prodScript =
      "https://assets.adobedtm.com/3010f46ab2ec/a3cbfcd83a52/launch-4688d17a7429.min.js"
    const lowerEnvScript =
      "https://assets.adobedtm.com/3010f46ab2ec/a3cbfcd83a52/launch-01b1cb6f5edd-development.min.js"
    const scriptSrc =
      process.env.REACT_APP_NODE_ENV === `prod` ? prodScript : lowerEnvScript
    const isScript = ssrWindow.document.getElementById("analyticScript")
    this.setState({
      open: true,
    })
    isScript && isScript.remove()
    const script = ssrWindow.document.createElement("script")
    script.src = scriptSrc
    script.id = "analyticScript"
    script.async = true
    ssrWindow.document.body.appendChild(script)
    if (this.props.isExternalClick) {
      this.props.handleModal(true)
    }
  }

  handleCloseModal = () => {
    this.setState({
      open: false,
    })

    if (this.props.isExternalClick) {
      this.props.handleModal(false)
    }
  }

  // Helper function to check if the slide is visible
  isSlideVisible = () => {
    const slideElement = this.videoRef.current?.closest(".slick-slide")
    return slideElement?.classList.contains("slick-active")
  }

  render() {
    const { classes } = this.props
    const {
      mediaSrc,
      videoThumbnailSrc,
      source,
      playIcon,
      ariaLabel,
      videoThumbnailAltText,
      videoPlayButtonAltText,
    } = this.props

    let { open } = this.state
    const { isExternalClick } = this.props
    if (isExternalClick) {
      open = this.props.open
    }

    const isActive = this.videoRef.current?.className.includes(`active`)
    const bodyWidth =
      ssrWindow.innerWidth > 0 ? ssrWindow.innerWidth : ssrWindow.screen.width

    // Determine tabIndex based on slide visibility
    const tabIndex = this.isSlideVisible() ? "0" : "-1"

    return (
      <div
        className={`Video ${classes.videoStyl}`}
        data-testid="Video"
        ref={this.videoRef}
      >
        {playIcon ? (
          <Button
            testid="Video - playButton"
            className={`${classes.buttonStyl} ${
              !this.state.open ? `d-flex` : `d-none`
            } justify-content-center align-items-center p-0 play-icon-wrapper`}
            onClick={this.handleOpenModal}
            ariaLabel={videoPlayButtonAltText || ariaLabel}
            analytics_key={`open video ${mediaSrc}`}
            ariaHasPopup="dialog"
            tabIndex={tabIndex}
          >
            <ReactSVG wrapper="span" src={getContentUrl(playIcon)} />
          </Button>
        ) : null}
        <div
          className="h-100 picture"
          role="img"
          aria-label={videoThumbnailAltText}
          style={{
            background: `url(${getContentUrl(videoThumbnailSrc)})`,
            backgroundSize: `cover`,
            maxWidth: "628px",
          }}
        />
        <GenericModal
          id={`video-${mediaSrc}`}
          open={open}
          onClose={this.handleCloseModal}
          customDialogueClass="bg-black p-0 position-relative"
          useFlexibleWidth
        >
          <ReactPlayer
            data-testid="Video - ReactPlayer"
            playing={this.state.isPlaying && isActive}
            className={source === `youtube` ? `youtube-player` : `video-player`}
            style={{ maxWidth: `${bodyWidth - 40}px` }}
            url={mediaSrc}
            controls
            onPause={() => {
              this.state.isPlaying &&
                this.setState(() => ({
                  isPlaying: false,
                }))
            }}
            config={{
              youtube: {
                embedOptions: {
                  host: "https://www.youtube-nocookie.com",
                },
              },
            }}
            onPlay={() => {
              !this.state.isPlaying &&
                this.setState(() => ({
                  isPlaying: true,
                }))
            }}
          />
          <Button
            testid="Video - closeButton"
            ariaLabel="close video"
            analytics_key={`close video ${mediaSrc}`}
            overrideClass="bg-transparent d-flex align-items-center justify-content-center"
            tabIndex={open ? `0` : `-1`}
            onClick={this.handleCloseModal}
            style={{
              borderRadius: `1.1rem`,
              height: `2.2rem`,
              opacity: 0.8,
              position: `absolute`,
              right: `0rem`,
              top: `0.5rem`,
              width: `2.2rem`,
            }}
          >
            <div
              style={{
                position: `relative`,
                top: `.1rem`,
              }}
            >
              <ReactSVG
                wrapper="span"
                src={getContentUrl(
                  "/content/dam/wm/icons/white-close-x-icon.svg",
                )}
              />
            </div>
          </Button>
        </GenericModal>
      </div>
    )
  }
}

Video.propTypes = {
  mediaSrc: PropTypes.string.isRequired,
  playIcon: PropTypes.string.isRequired,
  videoThumbnailSrc: PropTypes.string,
  source: PropTypes.string,
  open: PropTypes.bool,
  isExternalClick: PropTypes.bool,
  handleModal: PropTypes.func,
  ariaLabel: PropTypes.string,
  classes: PropTypes.object.isRequired,
}

Video.defaultProps = {
  open: false,
  handleModal: noop,
  videoThumbnailSrc: ``,
  source: ``,
  isExternalClick: false,
  ariaLabel: `open video`,
}

export default withStyles(Video, styles, { withTheme: true })
